import type {
  CalibrationLogEntry, EducationContext, Evaluation, EvidenceSource, Profile, Question, ResponseValue, SelfEstimate, SubjectId,
} from '@/core/types';
import { Store, MemoryRepository, LocalStorageRepository } from '@/core/store';
import type { AppState } from '@/core/state';
import { clock, uid } from '@/core/util/misc';
import { installProcessors } from '@/engine/processors';
import { evaluate } from '@/engine/questions/evaluate';
import { generateQuestion } from '@/engine/questions/generate';
import { eligibilityOf, viewsOf } from '@/engine/views';
import {
  completeSubject, currentSubject, nextItem, recordCalibrationAnswer, startCalibration, LENGTHS, type AdaptationDecision,
} from '@/engine/calibration';
import { catalog } from '@/data/catalog';

/**
 * Application services: the only way UI (and tests) change learning state.
 * Every function goes through the event pipeline — no screen keeps its own
 * learning state.
 */

export function createStore(initial?: AppState, persistent = false): Store {
  const s = new Store(persistent ? new LocalStorageRepository() : new MemoryRepository(), initial);
  installProcessors(s);
  return s;
}

export interface AnswerOptions {
  source: EvidenceSource;
  hintsUsed?: number;
  responseMs?: number;
  confidence?: number;
  sessionId?: string;
  reasoning?: string;
  /** A grade made elsewhere (Claude for open written answers); otherwise the question is evaluated locally. */
  evaluation?: Evaluation;
}

export function answerQuestion(store: Store, q: Question, response: ResponseValue | null, o: AnswerOptions): Evaluation {
  const evaluation = o.evaluation ?? evaluate(q, response);
  store.dispatch({
    type: 'QUESTION_ANSWERED', question: q, response, evaluation, source: o.source, hintsUsed: o.hintsUsed ?? 0,
    responseMs: o.responseMs ?? 20_000, confidence: o.confidence, sessionId: o.sessionId, reasoning: o.reasoning,
  });
  return evaluation;
}

export interface NewProfileInput {
  name: string;
  education: EducationContext;
  subjects: SubjectId[];
  selfEstimates: Partial<Record<SubjectId, SelfEstimate>>;
  dailyMinutes: number;
  priorities: string[];
  isDemo?: boolean;
}

/**
 * Creating a profile sets the curriculum universe and the calibration priors.
 * It does NOT create any skill estimate — every skill starts UNKNOWN.
 */
export function createProfile(store: Store, input: NewProfileInput): Profile {
  const profile: Profile = {
    id: uid('profile'),
    name: input.name,
    createdAt: clock.now(),
    education: input.education,
    subjects: input.subjects,
    selfEstimates: input.selfEstimates,
    dailyMinutes: input.dailyMinutes,
    priorities: input.priorities,
    onboarded: true,
    isDemo: !!input.isDemo,
    educationHistory: [],
  };
  store.dispatch({ type: 'PROFILE_CREATED', profile });
  return profile;
}

/** Stage/pathway change: relevance changes; evidence and estimates are preserved. */
export function changeEducation(store: Store, education: EducationContext) {
  store.dispatch({ type: 'EDUCATION_CHANGED', education });
}

/* ------------------------------------------------------------------ */
/* Calibration                                                          */
/* ------------------------------------------------------------------ */

export function beginCalibration(store: Store, subjects?: SubjectId[], length?: keyof typeof LENGTHS) {
  const s = store.getState();
  if (!s.profile) throw new Error('No profile');
  const list = subjects?.length ? subjects : s.profile.subjects;
  const run = startCalibration(list, s.profile.selfEstimates, eligibilityOf(s), length ?? s.settings.calibrationLength, clock.now());
  store.dispatch({ type: 'CALIBRATION_STARTED', run });
  return run;
}

export type CalibrationStep =
  | { kind: 'question'; decision: AdaptationDecision; question: Question; subjectId: SubjectId; asked: number; max: number }
  | { kind: 'done' };

export function calibrationNext(store: Store): CalibrationStep {
  for (let guard = 0; guard < 40; guard++) {
    const s = store.getState();
    const run = s.calibration.active;
    if (!run || run.status === 'completed') return { kind: 'done' };
    const now = clock.now();
    const views = viewsOf(s, now);
    const elig = eligibilityOf(s);
    const { run: r2, decision } = nextItem(run, elig, views, s.estimates);
    if (!decision) {
      const { run: r3, summary } = completeSubject(r2, views, s.estimates, elig, now);
      store.dispatch({ type: 'CALIBRATION_RUN_UPDATED', run: r3 });
      store.dispatch({ type: 'CALIBRATION_COMPLETED', subjectId: summary.subjectId, summary });
      if (r3.status === 'completed') {
        store.dispatch({ type: 'CALIBRATION_FINISHED' });
        return { kind: 'done' };
      }
      continue;
    }
    const question = generateQuestion({ skillId: decision.skillId, difficulty: decision.difficulty, mode: 'diagnostic', recent: s.exposure[decision.skillId] ?? [] });
    const sub = currentSubject(r2)!;
    if (!question) {
      sub.askedSkills[decision.skillId] = 2; // cannot generate — do not select it again
      store.dispatch({ type: 'CALIBRATION_RUN_UPDATED', run: r2 });
      continue;
    }
    store.dispatch({ type: 'CALIBRATION_RUN_UPDATED', run: r2 });
    return { kind: 'question', decision, question, subjectId: sub.subjectId, asked: sub.asked, max: sub.maxQuestions };
  }
  return { kind: 'done' };
}

export function calibrationSubmit(
  store: Store, decision: AdaptationDecision, question: Question, response: ResponseValue | null, responseMs: number, confidence?: number, graded?: Evaluation,
): { evaluation: Evaluation; message: string; direction: AdaptationDecision['direction'] } {
  const evaluation = answerQuestion(store, question, response, { source: 'diagnostic', responseMs, confidence, evaluation: graded });
  const s = store.getState();
  const run = s.calibration.active;
  if (!run) return { evaluation, message: '', direction: 'same' };
  const now = clock.now();
  const elig = eligibilityOf(s);
  const views = viewsOf(s, now);
  const res = recordCalibrationAnswer(run, decision, evaluation.correct, evaluation.misconceptionId, elig, views, s.estimates);
  const skill = catalog.skill(decision.skillId)!;
  const entry: CalibrationLogEntry = {
    at: now, subjectId: skill.subjectId, skillId: skill.id, domainId: skill.domainId, difficulty: decision.difficulty,
    correct: evaluation.correct, reason: decision.reason, message: res.message, failureType: res.failureType,
  };
  store.dispatch({ type: 'CALIBRATION_QUESTION_ANSWERED', entry, run: res.run });
  store.dispatch({ type: 'CALIBRATION_ADAPTATION_CHANGED', subjectId: skill.subjectId, decision: { ...decision, message: res.message, direction: res.next } });
  if (res.gap) {
    store.dispatch({ type: 'PREREQUISITE_GAP_DETECTED', skillId: res.gap.skillId, prerequisiteId: res.gap.prerequisiteId, blocks: catalog.descendants(res.gap.prerequisiteId).slice(0, 12) });
  }
  return { evaluation, message: res.message, direction: res.next };
}

export function abandonCalibration(store: Store) {
  store.dispatch({ type: 'CALIBRATION_ABANDONED' });
}

/* ------------------------------------------------------------------ */

let singleton: Store | null = null;
export function studioStore(): Store {
  if (!singleton) singleton = createStore(undefined, typeof localStorage !== 'undefined');
  return singleton;
}
